import React from "react";
import styled from "styled-components";
import toast from "react-hot-toast";
import ReportsTableStatusColumm from "../../../components/tables/columns/ReportsTableStatusColumn";
import ClientReportTableRowColumn from "./ClientReportTableRowColumn";
import ClientReportTableRowEfficacyColumn from "./ClientReportTableRowEfficacyColumn";
import ReportsTableActionColumn from "../../../components/tables/columns/ReportsTableActionColumn";
import { useNavigate } from "react-router-dom";
import { DateDisplay, ProfileDisplay } from "../../../components";
import { TableLoadingRow } from "../../../components/tables/VirtualTable";
import { ThemedSpan } from "../../../components/utilities";
import { useClientReportsList } from "../../../hooks/ClientReportContext";
import { useReportPdfData } from "../../../hooks/useReportPdfData";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { openPdfViewerModal } from "../../../components/modals/ViewPdfModal";
import { ClientReportType } from "../../../types/ClientReportType";

const LargePopover = styled(Popover)`
    max-width: 500px;
`;

export type ClientReportTableRowProps = {
    data: ClientReportType[],
    dateFormat?: string
    index: number,
    style?: any
};

const ClientReportTableRow = (
    { data, index, style, dateFormat = "DD/MM/YYYY" } : Readonly<ClientReportTableRowProps>
) => {
    const { height } = style;
    const { getStatusDescription, openReport } = useClientReportsList()!;
    const { getPdfBytes } = useReportPdfData(data[index]?.masterAccountId, data[index]?.id);
    const navigate = useNavigate();

    // do not return a data row when the data has not yet been collected from the backend
    if (index >= data.length) {
		return <TableLoadingRow style={{ height }} />
	}

    const {
        documentId,
        documentPdfId,
        typeDescription, 
        reportName,
        projectId,
        projectName,
        status,
        statusDescription,
        authorId,
        efficacy,
        complianceUserId,
        compliantDate,
        sentDate,
        createdDate,
        businessSheet,
        isPdfGenerated,
        isStatusTerminated,
        sentByMediaType
    } = data[index];

    const handleOpenDocument = (e: any) : void => {
        if (e && typeof (e.preventDefault) === 'function') {
            e.preventDefault();
        }
        if (isStatusTerminated === false && openReport && typeof(openReport) === 'function') {
            openReport(documentId, reportName);
            return;
        }
        if (isStatusTerminated === true && isPdfGenerated === true && documentPdfId) {
            getPdfBytes().then(
                r => openPdfViewerModal({ data: r, title: data[index].reportName })
            );
            return;
        }
        toast.error('Could not open report as unable to find the document to open.');
    };

    const handleOpenProject = (e: any) : void => {
        if (e && typeof (e.preventDefault) === 'function') {
            e.preventDefault();
        }
        navigate(`../projects?projectId=${projectId}`, { relative: 'path' });
    };

    return (
        <tr>
            {/* Report Name & Type */}
            <ClientReportTableRowColumn width={21.5} style={{ height }}>
                <p className="text-truncate m-0 lh-1">
                    <OverlayTrigger
                        placement="auto"
                        overlay={
                            <Popover>
                                <Popover.Body>
                                    {reportName}
                                </Popover.Body>
                            </Popover>
                        }
                    >
                        <a href={`open?id=${documentId}`} onClick={handleOpenDocument}>{reportName}</a>
                    </OverlayTrigger>
                    <br/>
                    <ThemedSpan variant="muted" className="fs-smallest">{typeDescription}</ThemedSpan>
                </p>
            </ClientReportTableRowColumn>
            {/* Project */}
            <ClientReportTableRowColumn width={12.5}>
                <a href={`../projects?projectId=${projectId}`} onClick={handleOpenProject}>{projectName}</a>
            </ClientReportTableRowColumn>
            {/* Status */}
            <ClientReportTableRowColumn width={15}>
                <ReportsTableStatusColumm data={data[index]} getStatusDescription={getStatusDescription} />
            </ClientReportTableRowColumn>
            {/* Author */}
            <ClientReportTableRowColumn width={11.25}>
                {authorId ? (<ProfileDisplay userId={authorId} />) : 'Author not assigned'}
            </ClientReportTableRowColumn>
            {/* Efficacy */}
            <ClientReportTableRowEfficacyColumn width={5} efficacy={efficacy} />
            {/* Compliance User / Officer Info */}
            <ClientReportTableRowColumn width={11.25}>
                {complianceUserId ? (<ProfileDisplay userId={complianceUserId} />) : (<span>-</span>)}
            </ClientReportTableRowColumn>
            {/* The date the report was made compliant */}
            <ClientReportTableRowColumn width={7}>
                {compliantDate ? (<DateDisplay format="DD/MM/YYYY">{compliantDate}</DateDisplay>) : (<span>-</span>)}
            </ClientReportTableRowColumn>
            {/* The date the report was sent out to the client */}
            <ClientReportTableRowColumn width={7}>
                {sentDate ? (<DateDisplay format="DD/MM/YYYY">{sentDate}</DateDisplay>) : (<span>-</span>)}
            </ClientReportTableRowColumn>
            {/* The date the report was created */}
            <ClientReportTableRowColumn width={7}>
                <DateDisplay format={dateFormat}>{createdDate}</DateDisplay>
            </ClientReportTableRowColumn>
            {/* Action Column  */}
            <ReportsTableActionColumn width={2.5} data={data[index]} />
        </tr>
    );
};

export default ClientReportTableRow;